import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, Sparkles, AlertTriangle, ArrowRight, Play, Star, ExternalLink, Activity, Award } from "lucide-react";

export default function HomeView() {
  const [isPlaying, setIsOpenVideo] = useState(false);

  const problemStats = [
    { value: "68%", text: "of enterprise leaders lack real-time visibility into supplier/vendor risks" },
    { value: "42%", text: "of supply chain disruptions stem from unmonitored vendor-level compliance drops" },
    { value: "$12.4M", text: "average annual cost of unoptimized vendor contract and spend overlap" }
  ];

  const platformPillars = [
    {
      title: "Vendor Risk Intelligence",
      desc: "Instant composite risk scoring (0-100) monitoring financial, delivery, compliance, and cyber factors in real-time.",
      link: "/platform/risk"
    },
    {
      title: "Spend Analytics & Cost Intelligence",
      desc: "Identify maverick spend, eliminate overlapping licensing, and negotiate better terms with automated consolidation insight.",
      link: "/platform/spend"
    },
    {
      title: "Compliance & Audit Automation",
      desc: "Centralize certification tracking, automate KYC/AML, and secure real-time proof-of-insurance verification.",
      link: "/platform/compliance"
    }
  ];

  const industries = [
    { name: "Manufacturing", desc: "SLA, logistics, and resource tracking for heavy operations.", path: "/industries/manufacturing" },
    { name: "Retail & E-commerce", desc: "Omnichannel inventory, vendor cost control, and merchant monitoring.", path: "/industries/retail" },
    { name: "Healthcare & Pharma", desc: "Strict HIPAA, regulatory compliance, and medical vendor audits.", path: "/industries/healthcare" },
    { name: "BFSI", desc: "High-level risk analytics, secure compliance automation, and SOC2 audits.", path: "/industries/bfsi" },
    { name: "IT & Technology", desc: "SaaS vendor sprawl detection, security monitoring, and licensing tracking.", path: "/industries/it" },
    { name: "Logistics & Supply Chain", desc: "Real-time delivery performance scoring, fuel index tracking, and routing analytics.", path: "/industries/logistics" }
  ];

  const howItWorks = [
    { step: "01", title: "Connect Vendor Data", desc: "Securely aggregate ERP, CRM, and accounts data using automated workflows." },
    { step: "02", title: "AI Scores & Monitors", desc: "Our platform continuously parses activity to maintain real-time composite scorecards." },
    { step: "03", title: "Get Alerts & Insights", desc: "Immediate warning signals sent when critical delivery or compliance parameters drop." },
    { step: "04", title: "Act with Confidence", desc: "Execute bulk updates, renegotiate terms, or trigger automated contract workflows." }
  ];

  const testimonials = [
    {
      quote: "Aroveniq's Vendor Intelligence transformed how we monitor our global manufacturing partners. The automated compliance scorecards saved us countless hours of manual audit.",
      author: "Sarah Jenkins",
      role: "VP of Global Procurement",
      company: "Apex Industrials",
      rating: 5
    },
    {
      quote: "We consolidated over $4.2M in overlapping technology licensing within 90 days using Aroveniq's Spend Analytics dashboard. An absolute game changer.",
      author: "Marcus Vance",
      role: "Director of Strategic Sourcing",
      company: "TechNexus Global",
      rating: 5
    }
  ];

  const integrations = [
    "SAP Ariba", "Coupa", "Oracle NetSuite", "Zoho Books", "QuickBooks", "Microsoft Dynamics", "Salesforce", "HubSpot"
  ];

  return (
    <div className="bg-[#05050A] text-white min-h-screen font-body relative overflow-hidden">
      
      {/* Background radial gradient glows */}
      <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-[#7C3AED]/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-80 right-1/4 w-[600px] h-[600px] bg-[#EA7C3D]/6 rounded-full blur-[160px] pointer-events-none" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-36 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text */}
          <div className="lg:col-span-6 space-y-8">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide" data-testid="hero-pill">
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              <span>Next-Generation Vendor Intelligence Platform</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none font-display text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-[#ABA3C2]" data-testid="hero-headline">
              Smarter Vendors.<br />
              <span className="text-[#7C3AED]">Stronger Supply Chains.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-[#ABA3C2] font-body max-w-xl leading-relaxed" data-testid="hero-subhead">
              Aroveniq gives procurement and supply-chain teams a single, real-time intelligence layer on every vendor they work with — risk, performance, spend, and compliance, unified.
            </p>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-demo"
                className="relative px-8 py-3.5 rounded-full bg-gradient-to-r from-[#4B2E83] via-[#7C3AED] to-[#EA7C3D] text-white text-center font-semibold hover:shadow-[0_0_25px_rgba(124,58,237,0.5)] transition-all duration-300 group"
              >
                Book a Demo
                <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-200" />
              </Link>
              <button
                onClick={() => setIsOpenVideo(true)}
                data-testid="hero-cta-video"
                className="px-8 py-3.5 rounded-full border border-white/10 text-center font-semibold hover:bg-white/5 transition-all duration-200 text-white flex items-center justify-center space-x-2"
              >
                <Play className="w-4 h-4 fill-current text-[#EA7C3D]" />
                <span>Watch Overview</span>
              </button>
            </div>
          </div>

          {/* Right Mockup Graphic */}
          <div className="lg:col-span-6 relative">
            <div className="relative group rounded-3xl p-1.5 bg-gradient-to-tr from-white/5 to-[#7C3AED]/20 border border-white/15 shadow-2xl backdrop-blur-xl" data-testid="hero-mockup">
              
              {/* Card glossy cover */}
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-3xl pointer-events-none" />
              
              {/* Illustrative Vendor Intelligence dashboard mockup */}
              <div className="bg-[#14101F]/90 rounded-2xl p-6 space-y-6 relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-[#EF4444] rounded-full" />
                    <div className="w-3 h-3 bg-[#F5A623] rounded-full" />
                    <div className="w-3 h-3 bg-[#22C55E] rounded-full" />
                    <span className="text-xs text-zinc-500 font-mono ml-2">aroveniq_intelligence_v1.2</span>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded bg-[#7C3AED]/20 text-[#9F67FF] font-semibold">Active Monitoring</span>
                </div>

                {/* Score panel */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="text-xs text-zinc-500">Overall Vendor Health</div>
                    <div className="text-3xl font-bold text-white font-mono mt-1">94.2</div>
                    <div className="text-[10px] text-[#22C55E] mt-1 font-semibold flex items-center">
                      <span>↑ 2.4% vs last week</span>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="text-xs text-zinc-500">Watchlist Risk Vendors</div>
                    <div className="text-3xl font-bold text-white font-mono mt-1">03</div>
                    <div className="text-[10px] text-[#F5A623] mt-1 font-semibold">2 pending critical reviews</div>
                  </div>
                </div>

                {/* Fake alert feed */}
                <div className="space-y-2">
                  <div className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">Recent System Triggers</div>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-between">
                    <span className="text-xs text-red-400">Global Logistics Corp. SLA dropped below 85%</span>
                    <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded font-mono font-bold">CRITICAL</span>
                  </div>
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-between">
                    <span className="text-xs text-orange-400">MedSupply Inc. Certificate of Insurance is expiring</span>
                    <span className="text-[10px] bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded font-mono font-bold">WARNING</span>
                  </div>
                </div>
              </div>

              {/* Centered circular play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <button 
                  onClick={() => setIsOpenVideo(true)}
                  className="pointer-events-auto w-16 h-16 rounded-full bg-[#EA7C3D] hover:bg-[#F2955F] flex items-center justify-center shadow-lg transform transition-all group-hover:scale-110"
                >
                  <Play className="w-6 h-6 text-white fill-current translate-x-0.5" />
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Video Modal Placeholder */}
      {isPlaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden bg-[#0A0710] border border-white/10 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg text-white">Aroveniq Solutions Explainer Video</h3>
              <button onClick={() => setIsOpenVideo(false)} className="text-zinc-400 hover:text-white font-bold">✕ Close</button>
            </div>
            <div className="aspect-video bg-black/50 flex flex-col items-center justify-center rounded-xl relative border border-white/5">
              <svg viewBox="0 0 100 100" className="w-24 h-24 mb-4" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="50,10 90,90 10,90" fill="#4B2E83" />
                <polygon points="50,10 90,90 65,90" fill="#7C3AED" />
                <polygon points="68,68 85,90 55,90" fill="#EA7C3D" />
              </svg>
              <p className="text-sm text-zinc-400">Placeholder Video Player. Under Development.</p>
            </div>
          </div>
        </div>
      )}

      {/* Trusted-by strip */}
      <section className="py-12 border-y border-white/10 bg-[#0A0710]/50" data-testid="trusted-by-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Trusted by procurement teams at industry leaders</p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-40">
            <span className="text-lg font-bold font-display tracking-widest text-zinc-400">APEX IND</span>
            <span className="text-lg font-bold font-display tracking-widest text-zinc-400">GLOBLOGIX</span>
            <span className="text-lg font-bold font-display tracking-widest text-zinc-400">MEDCORE</span>
            <span className="text-lg font-bold font-display tracking-widest text-zinc-400">TECHNEXUS</span>
          </div>
        </div>
      </section>

      {/* Problem stat band */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="problem-stat-band">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {problemStats.map((stat, i) => (
            <div key={i} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl relative overflow-hidden group hover:border-[#7C3AED]/45 transition-colors duration-300">
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#7C3AED]/20 to-transparent rounded-bl-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="text-5xl lg:text-6xl font-black font-display text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D]">{stat.value}</div>
              <p className="mt-4 text-sm sm:text-base text-[#ABA3C2]/90 leading-relaxed font-body">{stat.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Platform Pillars */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-testid="platform-pillars">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-black font-display tracking-tighter">Unified Vendor Intelligence Platform</h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto">Get complete visibility and strategic cost insight with our robust enterprise-grade pillars.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {platformPillars.map((pillar, i) => (
            <div key={i} className="p-8 rounded-2xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl hover:shadow-[0_0_30px_rgba(124,58,237,0.15)] hover:border-[#7C3AED]/50 transition-all duration-300 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#4B2E83] to-[#7C3AED] flex items-center justify-center shadow-md">
                  <Shield className="w-6 h-6 text-[#EA7C3D]" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white font-display">{pillar.title}</h3>
                <p className="text-sm text-[#ABA3C2] leading-relaxed font-body">{pillar.desc}</p>
              </div>
              <div className="pt-6">
                <Link to={pillar.link} className="inline-flex items-center text-sm font-semibold text-[#EA7C3D] hover:text-[#F2955F] transition-colors group">
                  <span>Read More</span>
                  <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Our Story Split Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-white/10" data-testid="our-story-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <h2 className="text-3xl sm:text-4xl font-black font-display tracking-tighter">Turn Vendor Chaos Into Strategic Clarity</h2>
            <p className="text-sm sm:text-base text-[#ABA3C2] leading-relaxed font-body">
              At Aroveniq, we recognize that procurement is no longer just transactional. Modern supply chains are complex networks where risk, performance, spend, and compliance intersect daily.
            </p>
            <p className="text-sm sm:text-base text-[#ABA3C2]/80 leading-relaxed font-body">
              We empower industry leaders with clean data, real-time trigger alarms, and a predictive single pane of glass to protect operating margins and compliance.
            </p>
            <div className="pt-4">
              <Link to="/about" className="inline-flex items-center px-6 py-2.5 rounded-full border border-[#7C3AED] text-white hover:bg-white/5 font-semibold text-sm transition-all">
                Our Mission & Story
              </Link>
            </div>
          </div>
          
          {/* Right Signature Cut-Corner Graphic */}
          <div className="lg:col-span-6">
            <div 
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 80%, 80% 100%, 0 100%)" }}
              className="relative aspect-video rounded-3xl bg-gradient-to-tr from-[#4B2E83] to-[#EA7C3D] p-1 flex items-center justify-center shadow-2xl group overflow-hidden"
            >
              <div className="absolute inset-0 bg-[#0A0710]/90 rounded-[22px] flex items-center justify-center p-8 text-center space-y-4 flex-col">
                <svg viewBox="0 0 100 100" className="w-16 h-16 text-white" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="50,10 90,90 10,90" fill="#4B2E83" />
                  <polygon points="50,10 90,90 65,90" fill="#7C3AED" />
                  <polygon points="68,68 85,90 55,90" fill="#EA7C3D" />
                </svg>
                <h3 className="font-bold text-white text-lg font-display tracking-wide">AROVENIQ INTELLIGENCE ENGINE</h3>
                <p className="text-xs text-[#ABA3C2] max-w-sm">Aggregating thousands of real-time signals to automate complete procurement compliance.</p>
              </div>
              
              {/* Logo's orange triangle notch callback in cut-corner */}
              <div className="absolute bottom-0 right-0 w-12 h-12 bg-[#EA7C3D] flex items-center justify-center pointer-events-none" style={{ clipPath: "polygon(100% 0, 0 100%, 100% 100%)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="industries-section">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-black font-display tracking-tighter">Engineered for Critical Industries</h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto">See how Aroveniq fits your unique sector and operational challenges perfectly.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((ind, i) => (
            <Link
              key={i}
              to={ind.path}
              className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#EA7C3D]/50 hover:bg-white/10 transition-all duration-300 relative group flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-8 h-8 rounded-lg bg-zinc-800 text-[#EA7C3D] flex items-center justify-center font-bold">
                  {ind.name[0]}
                </div>
                <h3 className="text-lg font-bold text-white font-display group-hover:text-[#EA7C3D] transition-colors">{ind.name}</h3>
                <p className="text-xs text-[#ABA3C2] leading-relaxed font-body">{ind.desc}</p>
              </div>
              <div className="pt-4 flex items-center text-xs text-zinc-400 group-hover:text-white font-semibold">
                <span>Explore Solutions</span>
                <ArrowRight className="ml-1 w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* How It Works with Timeline Dots Connected */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-white/10 relative" data-testid="how-it-works-section">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-black font-display tracking-tighter">Smarter Onboarding. Stronger Monitoring.</h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto">Get connected, audited, and protected in four clear, seamless milestones.</p>
        </div>
        
        {/* Horizontal timeline */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
          {howItWorks.map((step, i) => (
            <div key={i} className="space-y-4 relative">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] flex items-center justify-center text-white font-mono font-bold text-sm shadow-md">
                  {step.step}
                </div>
                <div className="hidden md:block flex-1 h-[2px] bg-dashed bg-gradient-to-r from-[#EA7C3D] to-transparent" />
              </div>
              <h3 className="text-lg font-bold text-white font-display">{step.title}</h3>
              <p className="text-xs text-[#ABA3C2] leading-relaxed font-body">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Carousel / Grid */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-white/10" data-testid="testimonials-section">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-black font-display tracking-tighter">Approved by Procurement Leaders</h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mx-auto">Enterprise operators depend on Aroveniq to defend their supply networks.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((test, i) => (
            <div key={i} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="flex space-x-1">
                  {[...Array(test.rating)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 text-orange-400 fill-current" />
                  ))}
                </div>
                <p className="text-sm sm:text-base text-zinc-200 italic font-body leading-relaxed">&ldquo;{test.quote}&rdquo;</p>
              </div>
              <div className="flex items-center space-x-3 pt-4 border-t border-white/5">
                <div>
                  <div className="text-sm font-bold text-white font-display">{test.author}</div>
                  <div className="text-xs text-zinc-500 font-mono">{test.role} · {test.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations Marquee */}
      <section className="py-16 bg-white/5 border-y border-white/15 overflow-hidden" data-testid="integrations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#EA7C3D]">Universal Enterprise ERP & Accounting Connections</p>
          <div className="flex space-x-8 justify-center items-center opacity-60 flex-wrap gap-y-4">
            {integrations.map((item, i) => (
              <span key={i} className="text-sm font-mono tracking-wider font-bold bg-white/5 px-4 py-2 rounded-lg border border-white/5">{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="final-cta-section">
        <div className="rounded-3xl p-12 bg-gradient-to-r from-[#2C1B4D] via-[#4B2E83] to-[#EA7C3D]/80 border border-white/10 text-center space-y-6 shadow-2xl relative overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#7C3AED]/20 rounded-full blur-[100px] pointer-events-none" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-display tracking-tighter max-w-2xl mx-auto leading-tight">Ready to see your vendor risk in one dashboard?</h2>
          <p className="text-sm sm:text-base text-[#ABA3C2]/90 max-w-lg mx-auto">
            Book an interactive architectural deep-dive with our procurement specialists today.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="inline-flex px-8 py-4 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 font-bold text-sm tracking-wide transition-all shadow-lg items-center"
            >
              Book your Demo Session
              <ArrowRight className="ml-2 w-4 h-4 text-[#EA7C3D]" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}